import { isMobile } from '@gv/triage-components';

import { getToken } from 'utils/storage';

/**
 * Mobile-friendly file download utility
 * Uses fetch + blob approach for better compatibility with mobile Chrome
 * which has restrictions on programmatic downloads
 */
export const downloadFile = async (
  url: string,
  filename: string
): Promise<void> => {
  const mobile = isMobile();
  const isChromeMobile =
    mobile &&
    /Chrome/i.test(navigator.userAgent) &&
    /Mobile/i.test(navigator.userAgent);

  try {
    if (isChromeMobile) {
      // For mobile Chrome, use fetch + blob approach
      // This is more reliable than direct anchor tag downloads
      const token = getToken('accessToken');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if token exists and URL is from same origin
      if (token && url.startsWith(window.location.origin)) {
        headers.authorization = token;
      }

      const response = await fetch(url, {
        headers,
        method: 'GET',
        // Ensure credentials are included for same-origin requests
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      // For desktop and other browsers, use the standard approach
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Download error:', error);
    // Fallback: try opening in new tab if download fails
    if (isChromeMobile) {
      window.open(url, '_blank');
    } else {
      throw error;
    }
  }
};
